/**
 * POS Products Service
 *
 * API calls for POS Products feature.
 */

import { apiClient } from '@/api/client';
import type {
  PosProductListParams,
  PosProductListItem,
  PosProductDetail,
  PosProductStats,
  CategoryCount,
  PosSyncResult,
  PosSyncStatusInfo,
  PosProductsListResponse,
  // API Response types
  ApiSuccess,
  PosProductDetailResponse,
  PosProductStatsResponse,
  PosProductCategoriesResponse,
  PosSyncResponse,
  PosSyncStatusResponse,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  PosProductListParams,
  PosProductListItem,
  PosProductDetail,
  PosProductStats,
  CategoryCount,
  PosSyncResult,
  PosSyncStatusInfo,
  PosProductsListResponse,
};

// Re-export enums
export {
  PosProductType,
  SyncStatus,
  PosProductSortField,
  SortOrder,
} from '@bstock/shared';

export const posProductsService = {
  /**
   * List POS products with pagination and filters
   */
  async list(params: PosProductListParams): Promise<PosProductsListResponse> {
    const response = await apiClient.get<ApiSuccess<PosProductsListResponse>>('/pos-products', { params });
    return response.data.data;
  },

  /**
   * Get single POS product by ID
   */
  async getById(id: string): Promise<PosProductDetailResponse> {
    const response = await apiClient.get<ApiSuccess<PosProductDetailResponse>>(`/pos-products/${id}`);
    return response.data.data;
  },

  /**
   * Get product statistics
   */
  async getStats(): Promise<PosProductStatsResponse> {
    const response = await apiClient.get<ApiSuccess<PosProductStatsResponse>>('/pos-products/stats');
    return response.data.data;
  },

  /**
   * Get distinct categories
   */
  async getCategories(): Promise<PosProductCategoriesResponse> {
    const response = await apiClient.get<ApiSuccess<PosProductCategoriesResponse>>('/pos-products/categories');
    return response.data.data;
  },

  /**
   * Trigger manual synchronization
   */
  async sync(): Promise<PosSyncResponse> {
    const response = await apiClient.post<ApiSuccess<PosSyncResponse>>('/pos-products/sync');
    return response.data.data;
  },

  /**
   * Get sync status
   */
  async getSyncStatus(): Promise<PosSyncStatusResponse> {
    const response = await apiClient.get<ApiSuccess<PosSyncStatusResponse>>('/pos-products/sync/status');
    return response.data.data;
  },
};
